import { useEffect, useMemo, useRef, useState } from "react";
import type { ModelRow, ProviderView } from "../lib/api";
import {
  isDemoModel,
  modelMatchesFilter,
  modelMatchesQuery,
  recommendedScore,
  sortModels,
  type ModelFilterId,
  type ModelView,
} from "../lib/model-display";
import { POLICY_META, POLICY_ORDER } from "../lib/developer";
import { Dot, Modal } from "./ui";

const MODEL_PAGE = 30;

const CATS: Array<{ id: ModelFilterId; label: string }> = [
  { id: "all", label: "All" },
  { id: "free", label: "Free" },
  { id: "paid", label: "Paid" },
  { id: "coding", label: "Coding" },
  { id: "vision", label: "Vision" },
  { id: "tools", label: "Tools" },
  { id: "embeddings", label: "Embeddings" },
  { id: "fast", label: "Fast" },
];

/**
 * Professional model picker: NEXUS routing policies first, then real provider
 * models — search, category filters, capped list. Virtual policies and real
 * deployments are visually distinct.
 */
export function ModelSelector({
  models,
  providers,
  value,
  onChange,
  placeholder = "Select a model or routing policy",
}: {
  models: ModelView[];
  providers: ProviderView[];
  value: string;
  onChange: (modelId: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<ModelFilterId>("all");
  const [shown, setShown] = useState(MODEL_PAGE);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
    else {
      setQuery("");
      setCat("all");
      setShown(MODEL_PAGE);
    }
  }, [open]);

  const selected = useMemo(() => {
    if (POLICY_ORDER.includes(value as (typeof POLICY_ORDER)[number])) {
      return { kind: "policy" as const, meta: POLICY_META[value] };
    }
    const m = models.find((x) => x.logicalId === value);
    return m ? { kind: "model" as const, model: m } : null;
  }, [value, models]);

  const prodModels = useMemo(() => models.filter((m) => !isDemoModel(m)), [models]);

  const list = useMemo(() => {
    let out = prodModels.filter((m) => modelMatchesFilter(m, cat === "all" ? "all" : cat));
    out = out.filter((m) => modelMatchesQuery(m, query));
    return sortModels(out, "recommended");
  }, [prodModels, cat, query]);

  const policies = useMemo(() => {
    const q = query.trim().toLowerCase();
    return POLICY_ORDER.filter((id) => {
      const meta = POLICY_META[id];
      if (!meta) return false;
      if (!q) return true;
      return (
        id.includes(q) ||
        meta.name.toLowerCase().includes(q) ||
        meta.tagline.toLowerCase().includes(q)
      );
    });
  }, [query]);

  const providerFor = (m: ModelRow) =>
    providers.find((p) => p.providerTypeId === m.provider?.providerTypeId);

  return (
    <>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 rounded-lg border border-canvas-border bg-canvas px-3 py-2.5 text-left transition hover:border-accent/50"
        onClick={() => setOpen(true)}
      >
        {selected?.kind === "policy" ? (
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <span className="chip-accent !text-[10px]">NEXUS POLICY</span>
              <span className="truncate text-sm font-medium">
                {selected.meta?.name ?? value}
              </span>
              <span className="mono text-xs text-canvas-muted">{value}</span>
            </span>
            <span className="mt-0.5 block truncate text-xs text-canvas-muted">
              {selected.meta?.tagline}
            </span>
          </span>
        ) : selected?.kind === "model" ? (
          <span className="flex min-w-0 items-center gap-2.5">
            {(() => {
              const pv = providerFor(selected.model);
              return pv ? (
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-[10px] font-bold"
                  style={{ background: `${pv.definition.branding.accent}22`, color: pv.definition.branding.accent }}
                >
                  {pv.definition.branding.letter}
                </span>
              ) : null;
            })()}
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">
                {selected.model.displayName}
              </span>
              <span className="block truncate text-xs text-canvas-muted">
                {selected.model.provider?.displayName ?? selected.model.provider?.providerTypeId}
                {selected.model.pricing.tier === "free" ? " · Free" : ""}
              </span>
            </span>
          </span>
        ) : (
          <span className="text-sm text-canvas-muted">{value || placeholder}</span>
        )}
        <span className="shrink-0 text-canvas-muted">▾</span>
      </button>

      {open && (
        <Modal title="Choose model or routing policy" onClose={() => setOpen(false)} wide>
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search — “free”, “vision”, “llama”, “coding”…"
            className="mb-3 w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 text-sm outline-none focus:border-accent"
          />
          <div className="mb-4 flex flex-wrap gap-1.5">
            {CATS.map((c) => (
              <button
                key={c.id}
                className={`chip ${cat === c.id ? "chip-accent" : "chip-muted"} cursor-pointer !py-1 text-xs`}
                onClick={() => {
                  setCat(c.id);
                  setShown(MODEL_PAGE);
                }}
              >
                {c.label}
              </button>
            ))}
          </div>

          {policies.length > 0 && (
            <div className="mb-4">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-canvas-muted">
                NEXUS routing policies — let NEXUS choose
              </div>
              <div className="space-y-1.5">
                {policies.map((id) => {
                  const meta = POLICY_META[id]!;
                  return (
                    <button
                      key={id}
                      className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition ${
                        value === id
                          ? "border-accent bg-accent/10"
                          : "border-canvas-border hover:border-accent/40"
                      }`}
                      onClick={() => {
                        onChange(id);
                        setOpen(false);
                      }}
                    >
                      <span className="min-w-0">
                        <span className="flex items-center gap-2">
                          <span className="chip-accent !text-[10px]">POLICY</span>
                          <span className="text-sm font-medium">{meta.name}</span>
                          <span className="mono text-xs text-canvas-muted">{id}</span>
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-canvas-muted">
                          {meta.tagline}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-canvas-muted">
              Model catalog — {list.length} deployments
            </div>
            {list.length === 0 && (
              <p className="py-6 text-center text-sm text-canvas-muted">
                No models match this filter.
              </p>
            )}
            <div className="space-y-1">
              {list.slice(0, shown).map((m) => {
                const pv = providerFor(m);
                return (
                  <button
                    key={m.id}
                    className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition ${
                      value === m.logicalId
                        ? "border-accent bg-accent/10"
                        : "border-transparent hover:border-canvas-border hover:bg-canvas-raised"
                    }`}
                    onClick={() => {
                      onChange(m.logicalId);
                      setOpen(false);
                    }}
                  >
                    {pv && (
                      <span
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-[10px] font-bold"
                        style={{ background: `${pv.definition.branding.accent}22`, color: pv.definition.branding.accent }}
                      >
                        {pv.definition.branding.letter}
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">{m.displayName}</span>
                      <span className="block truncate text-xs text-canvas-muted">
                        {m.provider?.displayName ?? m.provider?.providerTypeId} ·{" "}
                        {m.pricing.tier === "free" ? "Free" : "Paid"} ·{" "}
                        {(m.contextWindow / 1000).toFixed(0)}K ctx
                      </span>
                    </span>
                    {pv && (
                      <Dot
                        tone={
                          pv.health?.state === "healthy"
                            ? "ok"
                            : pv.health?.state === "degraded"
                              ? "warn"
                              : "muted"
                        }
                      />
                    )}
                  </button>
                );
              })}
            </div>
            {list.length > shown && (
              <button
                className="btn-ghost mt-2 w-full justify-center text-xs"
                onClick={() => setShown((s) => s + MODEL_PAGE)}
              >
                Show {Math.min(MODEL_PAGE, list.length - shown)} more — {list.length - shown} remaining
              </button>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
